// ========================================
// AI CHAT SIDEBAR COMPONENT
// ========================================

/**
 * Sliding sidebar chat interface for Ask Operations.
 * Allows users to ask questions about teams, users, budgets, and system health.
 * Query-only mode - no actions, just information queries.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { sendChatMessage, getChatStatus, generateSessionId, submitFeedback } from '../services/chatService';
import EntityCard from './chat/EntityCard';
import ActionCard from './chat/ActionCard';
import FeedbackModal from './chat/FeedbackModal';

// ========================================
// MARKDOWN RENDERER
// ========================================

/**
 * Simple markdown renderer for chat messages
 * Handles: tables, bold, italic, code, lists, headers
 */
const renderMarkdown = (text) => {
    if (!text) return null;

    const lines = text.split('\n');
    const elements = [];
    let i = 0;

    while (i < lines.length) {
        const line = lines[i];

        // Check for table - supports both formats:
        // 1. Lines starting with | (e.g., "| Header | Header |")
        // 2. Lines with | in middle (e.g., "Header | Header")
        // Next line must be a separator (--- | --- or |---|---|)
        const nextLine = lines[i + 1]?.trim() || '';
        const isTableSeparator = /^[-:\s|]+$/.test(nextLine) && nextLine.includes('|') && nextLine.includes('-');
        const hasTablePipes = line.includes('|');

        if (hasTablePipes && isTableSeparator) {
            const tableLines = [];
            // Collect all lines that contain | (table rows)
            while (i < lines.length && lines[i].includes('|')) {
                tableLines.push(lines[i]);
                i++;
            }
            elements.push(renderTable(tableLines, elements.length));
            continue;
        }

        // Header lines
        if (line.startsWith('### ')) {
            elements.push(
                <div key={elements.length} style={{ fontWeight: '600', fontSize: '0.9375rem', marginTop: '0.75rem', marginBottom: '0.25rem', color: 'var(--fg-primary)' }}>
                    {renderInlineMarkdown(line.substring(4))}
                </div>
            );
            i++;
            continue;
        }
        if (line.startsWith('## ')) {
            elements.push(
                <div key={elements.length} style={{ fontWeight: '600', fontSize: '1rem', marginTop: '0.75rem', marginBottom: '0.25rem', color: 'var(--fg-primary)' }}>
                    {renderInlineMarkdown(line.substring(3))}
                </div>
            );
            i++;
            continue;
        }

        // Bullet list
        if (line.trim().match(/^[-*]\s/)) {
            const listItems = [];
            while (i < lines.length && lines[i].trim().match(/^[-*]\s/)) {
                listItems.push(lines[i].trim().substring(2));
                i++;
            }
            elements.push(
                <ul key={elements.length} style={{ margin: '0.5rem 0', paddingLeft: '1.25rem' }}>
                    {listItems.map((item, idx) => (
                        <li key={idx} style={{ marginBottom: '0.25rem' }}>{renderInlineMarkdown(item)}</li>
                    ))}
                </ul>
            );
            continue;
        }

        // Numbered list
        if (line.trim().match(/^\d+\.\s/)) {
            const listItems = [];
            while (i < lines.length && lines[i].trim().match(/^\d+\.\s/)) {
                listItems.push(lines[i].trim().replace(/^\d+\.\s/, ''));
                i++;
            }
            elements.push(
                <ol key={elements.length} style={{ margin: '0.5rem 0', paddingLeft: '1.25rem' }}>
                    {listItems.map((item, idx) => (
                        <li key={idx} style={{ marginBottom: '0.25rem' }}>{renderInlineMarkdown(item)}</li>
                    ))}
                </ol>
            );
            continue;
        }

        // Empty line
        if (!line.trim()) {
            elements.push(<div key={elements.length} style={{ height: '0.5rem' }} />);
            i++;
            continue;
        }

        // Regular paragraph
        elements.push(
            <div key={elements.length} style={{ marginBottom: '0.25rem' }}>
                {renderInlineMarkdown(line)}
            </div>
        );
        i++;
    }

    return elements;
};

/**
 * Render markdown table
 */
const renderTable = (tableLines, keyBase) => {
    if (tableLines.length < 2) return null;

    // Parse header row
    const headerCells = tableLines[0].split('|').filter(cell => cell.trim()).map(cell => cell.trim());

    // Parse data rows (skip separator line at index 1)
    const dataRows = tableLines.slice(2).map(line =>
        line.split('|').filter(cell => cell.trim()).map(cell => cell.trim())
    );

    const tableStyle = {
        borderCollapse: 'collapse',
        fontSize: '0.8125rem',
        width: '100%',
        marginTop: '0.5rem',
        marginBottom: '0.5rem',
        backgroundColor: 'var(--bg)',
        borderRadius: '6px',
        overflow: 'hidden'
    };

    const thStyle = {
        padding: '0.5rem 0.625rem',
        textAlign: 'left',
        fontWeight: '600',
        backgroundColor: 'var(--border)',
        borderBottom: '1px solid var(--border)',
        color: 'var(--fg-primary)',
        whiteSpace: 'nowrap'
    };

    const tdStyle = {
        padding: '0.5rem 0.625rem',
        borderBottom: '1px solid var(--border)',
        color: 'var(--fg-primary)',
        whiteSpace: 'nowrap'
    };

    return (
        <div key={keyBase} style={{ overflowX: 'auto', marginTop: '0.5rem', marginBottom: '0.5rem' }}>
            <table style={tableStyle}>
                <thead>
                    <tr>
                        {headerCells.map((cell, idx) => (
                            <th key={idx} style={thStyle}>{cell}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {dataRows.map((row, rowIdx) => (
                        <tr key={rowIdx}>
                            {row.map((cell, cellIdx) => (
                                <td key={cellIdx} style={tdStyle}>{renderInlineMarkdown(cell)}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

/**
 * Render inline markdown (bold, italic, code)
 */
const renderInlineMarkdown = (text) => {
    if (!text) return null;

    // Split by markdown patterns and render
    const parts = [];
    let remaining = text;
    let key = 0;

    while (remaining) {
        // Bold **text**
        const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
        // Inline code `text`
        const codeMatch = remaining.match(/`([^`]+)`/);

        // Find earliest match
        let earliestMatch = null;
        let earliestIndex = remaining.length;
        let matchType = null;

        if (boldMatch && boldMatch.index < earliestIndex) {
            earliestMatch = boldMatch;
            earliestIndex = boldMatch.index;
            matchType = 'bold';
        }
        if (codeMatch && codeMatch.index < earliestIndex) {
            earliestMatch = codeMatch;
            earliestIndex = codeMatch.index;
            matchType = 'code';
        }

        if (earliestMatch) {
            // Add text before match
            if (earliestIndex > 0) {
                parts.push(remaining.substring(0, earliestIndex));
            }

            // Add formatted element
            if (matchType === 'bold') {
                parts.push(<strong key={key++}>{earliestMatch[1]}</strong>);
            } else if (matchType === 'code') {
                parts.push(
                    <code key={key++} style={{
                        backgroundColor: 'var(--bg)',
                        padding: '0.125rem 0.375rem',
                        borderRadius: '4px',
                        fontSize: '0.8125rem',
                        fontFamily: 'monospace',
                        color: '#a5f3fc'
                    }}>
                        {earliestMatch[1]}
                    </code>
                );
            }

            remaining = remaining.substring(earliestIndex + earliestMatch[0].length);
        } else {
            // No more matches, add remaining text
            parts.push(remaining);
            break;
        }
    }

    return parts.length === 1 ? parts[0] : parts;
};

// ========================================
// STYLES
// ========================================

const styles = {
    // Overlay behind sidebar
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'var(--overlay)',
        zIndex: 999,
        opacity: 0,
        visibility: 'hidden',
        transition: 'opacity 0.3s ease, visibility 0.3s ease'
    },
    overlayOpen: {
        opacity: 1,
        visibility: 'visible'
    },

    // Main sidebar container
    sidebar: {
        position: 'fixed',
        top: 0,
        right: 0,
        width: 'min(600px, 90vw)', // Wider for tables, but max 90% of viewport
        height: '100vh',
        backgroundColor: 'var(--bg)',
        borderLeft: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1000,
        transform: 'translateX(100%)',
        transition: 'transform 0.3s ease',
        boxShadow: '-4px 0 20px var(--overlay)'
    },
    sidebarOpen: {
        transform: 'translateX(0)'
    },

    // Header
    header: {
        padding: '1rem 1.25rem',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: 'var(--border)',
        flexShrink: 0
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem'
    },
    headerTitle: {
        margin: 0,
        fontSize: '1.125rem',
        fontWeight: '600',
        color: 'var(--fg-primary)',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
    },
    headerIcon: {
        fontSize: '1.25rem'
    },
    newChatButton: {
        backgroundColor: 'var(--border)',
        border: 'none',
        borderRadius: '6px',
        padding: '0.375rem 0.75rem',
        fontSize: '0.75rem',
        fontWeight: '500',
        color: 'var(--fg-primary)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '0.25rem',
        transition: 'background-color 0.2s'
    },
    closeButton: {
        background: 'none',
        border: 'none',
        fontSize: '1.5rem',
        color: 'var(--fg-secondary)',
        cursor: 'pointer',
        padding: '0.25rem',
        lineHeight: 1,
        borderRadius: '4px',
        transition: 'background-color 0.2s, color 0.2s'
    },

    // Status bar
    statusBar: {
        padding: '0.5rem 1.25rem',
        backgroundColor: 'var(--bg)',
        borderBottom: '1px solid var(--border)',
        fontSize: '0.75rem',
        color: 'var(--muted)',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        flexShrink: 0
    },
    statusDot: {
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: '#22c55e'
    },
    statusDotError: {
        backgroundColor: '#ef4444'
    },
    statusDotLoading: {
        backgroundColor: '#eab308',
        animation: 'pulse 1s ease-in-out infinite'
    },

    // Messages container
    messagesContainer: {
        flex: 1,
        overflowY: 'auto',
        padding: '1rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem'
    },

    // Welcome message
    welcomeMessage: {
        textAlign: 'center',
        padding: '2rem 1rem',
        color: 'var(--fg-secondary)'
    },
    welcomeTitle: {
        fontSize: '1.125rem',
        fontWeight: '600',
        color: 'var(--fg-primary)',
        marginBottom: '0.5rem'
    },
    welcomeText: {
        fontSize: '0.875rem',
        marginBottom: '1rem',
        lineHeight: '1.5'
    },

    // Suggested prompts
    suggestedPrompts: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        marginTop: '1rem'
    },
    suggestedPromptsInline: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.5rem',
        marginTop: '0.75rem',
        justifyContent: 'flex-start'
    },
    suggestedPrompt: {
        padding: '0.625rem 0.875rem',
        backgroundColor: 'var(--border)',
        border: '1px solid var(--border)',
        borderRadius: '8px',
        color: 'var(--fg-primary)',
        fontSize: '0.8125rem',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'background-color 0.2s, border-color 0.2s'
    },
    suggestedPromptSmall: {
        padding: '0.5rem 0.75rem',
        backgroundColor: 'var(--bg)',
        border: '1px solid var(--border)',
        borderRadius: '16px',
        color: 'var(--fg-secondary)',
        fontSize: '0.75rem',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'background-color 0.2s, border-color 0.2s, color 0.2s',
        whiteSpace: 'nowrap'
    },

    // Message bubbles
    message: {
        maxWidth: '85%',
        padding: '0.75rem 1rem',
        borderRadius: '12px',
        fontSize: '0.875rem',
        lineHeight: '1.5',
        wordBreak: 'break-word'
    },
    userMessage: {
        alignSelf: 'flex-end',
        backgroundColor: 'var(--border)',
        color: 'var(--fg-primary)',
        borderBottomRightRadius: '4px'
    },
    assistantMessage: {
        alignSelf: 'flex-start',
        backgroundColor: 'var(--border)',
        color: 'var(--fg-primary)',
        border: '1px solid var(--border)',
        borderBottomLeftRadius: '4px',
        maxWidth: '95%' // Wider for tables
    },
    errorMessage: {
        backgroundColor: '#7f1d1d',
        border: '1px solid #dc2626'
    },

    // Follow-up suggestions container
    followUpContainer: {
        alignSelf: 'flex-start',
        maxWidth: '90%',
        marginTop: '0.25rem'
    },
    followUpLabel: {
        fontSize: '0.6875rem',
        color: 'var(--muted)',
        marginBottom: '0.375rem',
        textTransform: 'uppercase',
        letterSpacing: '0.05em'
    },

    // Typing indicator
    typingIndicator: {
        alignSelf: 'flex-start',
        padding: '0.75rem 1rem',
        backgroundColor: 'var(--border)',
        border: '1px solid var(--border)',
        borderRadius: '12px',
        borderBottomLeftRadius: '4px',
        display: 'flex',
        gap: '4px'
    },
    typingDot: {
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: 'var(--muted)',
        animation: 'typing 1.4s ease-in-out infinite'
    },

    // Input container
    inputContainer: {
        padding: '1rem',
        borderTop: '1px solid var(--border)',
        backgroundColor: 'var(--bg)',
        flexShrink: 0
    },
    inputWrapper: {
        display: 'flex',
        gap: '0.5rem',
        alignItems: 'flex-end'
    },
    textarea: {
        flex: 1,
        padding: '0.75rem 1rem',
        backgroundColor: 'var(--bg)',
        border: '1px solid var(--border)',
        borderRadius: '8px',
        color: 'var(--fg-primary)',
        fontSize: '0.875rem',
        resize: 'none',
        minHeight: '44px',
        maxHeight: '120px',
        outline: 'none',
        fontFamily: 'inherit',
        lineHeight: '1.5'
    },
    sendButton: {
        padding: '0.75rem 1rem',
        backgroundColor: 'var(--border)',
        border: 'none',
        borderRadius: '8px',
        color: 'var(--fg-primary)',
        fontSize: '0.875rem',
        fontWeight: '500',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '44px',
        height: '44px'
    },
    sendButtonDisabled: {
        backgroundColor: 'var(--border)',
        cursor: 'not-allowed'
    },

    // Disclaimer
    disclaimer: {
        padding: '0.5rem 1rem',
        fontSize: '0.6875rem',
        color: 'var(--muted)',
        textAlign: 'center',
        borderTop: '1px solid var(--border)',
        backgroundColor: 'var(--bg)',
        flexShrink: 0
    }
};

// ========================================
// CONTEXT DETECTION
// ========================================

/**
 * Detect the context of the last message to show relevant follow-ups
 */
const detectContext = (messages) => {
    if (messages.length === 0) return 'default';

    // Look at the last few messages for context
    const recentMessages = messages.slice(-4).map(m => m.content.toLowerCase()).join(' ');

    if (recentMessages.includes('budget') || recentMessages.includes('spend') || recentMessages.includes('limit')) {
        return 'budget';
    }
    if (recentMessages.includes('health') || recentMessages.includes('status') || recentMessages.includes('unhealthy')) {
        return 'health';
    }
    if (recentMessages.includes('team') || recentMessages.includes('blocked') || recentMessages.includes('member')) {
        return 'teams';
    }
    if (recentMessages.includes('audit') || recentMessages.includes('action') || recentMessages.includes('log')) {
        return 'audit';
    }
    if (recentMessages.includes('user') || recentMessages.includes('account') || recentMessages.includes('disabled')) {
        return 'users';
    }

    return 'default';
};

// ========================================
// COMPONENT
// ========================================

const ChatSidebar = ({ isOpen, onClose, onOpenTeam, onOpenUser, onOpenSettings, onNavigate, onOpenHelp, visibleTeamIds }) => {
    const { t, i18n } = useTranslation();
    const language = i18n.language?.split('-')[0] || 'en'; // Handle 'en-US' -> 'en'

    // Suggested prompts for help context (FAQ-style)
    const SUGGESTED_PROMPTS = [
        t('chat.contextHelp.prompt1'),
        t('chat.contextHelp.prompt2'),
        t('chat.contextHelp.prompt3'),
        t('chat.contextHelp.prompt4'),
        t('chat.contextHelp.prompt5'),
        t('chat.contextHelp.prompt6')
    ];

    const FOLLOW_UP_SUGGESTIONS = {
        budget: [
            t('chat.followUp.budget1'),
            t('chat.followUp.budget2'),
            t('chat.followUp.budget3')
        ],
        health: [
            t('chat.followUp.health1'),
            t('chat.followUp.health2'),
            t('chat.followUp.health3')
        ],
        teams: [
            t('chat.followUp.teams1'),
            t('chat.followUp.teams2'),
            t('chat.followUp.teams3')
        ],
        audit: [
            t('chat.followUp.audit1'),
            t('chat.followUp.audit2'),
            t('chat.followUp.audit3')
        ],
        users: [
            t('chat.followUp.users1'),
            t('chat.followUp.users2'),
            t('chat.followUp.users3')
        ],
        default: [
            t('chat.followUp.default1'),
            t('chat.followUp.default2'),
            t('chat.followUp.default3')
        ]
    };

    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [serviceStatus, setServiceStatus] = useState(null);
    const [sessionId, setSessionId] = useState(() => generateSessionId());

    // Feedback state
    const [feedbackModal, setFeedbackModal] = useState({
        isOpen: false,
        rating: null,
        messageIndex: null,
        question: '',
        response: ''
    });
    const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
    const [feedbackGiven, setFeedbackGiven] = useState({}); // Track which messages have feedback

    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);

    // Scroll to bottom when new messages arrive
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, isLoading]);

    // Check service status when sidebar opens
    useEffect(() => {
        if (isOpen && !serviceStatus) {
            getChatStatus()
                .then(status => setServiceStatus(status))
                .catch(err => {
                    console.error('[CHAT] Status check failed:', err);
                    setServiceStatus({ status: 'error', error: err.message });
                });
        }
    }, [isOpen, serviceStatus]);

    // Focus textarea when sidebar opens
    useEffect(() => {
        if (isOpen && textareaRef.current) {
            setTimeout(() => textareaRef.current?.focus(), 300);
        }
    }, [isOpen]);

    // Start new conversation
    const handleNewChat = () => {
        setMessages([]);
        setSessionId(generateSessionId());
        setFeedbackGiven({}); // Reset feedback tracking
        if (textareaRef.current) {
            textareaRef.current.focus();
        }
    };

    // Open feedback modal
    const handleFeedbackClick = (rating, messageIndex) => {
        // Find the question that led to this response
        // The question is the user message just before this assistant message
        const question = messages[messageIndex - 1]?.content || '';
        const response = messages[messageIndex]?.content || '';

        setFeedbackModal({
            isOpen: true,
            rating,
            messageIndex,
            question,
            response
        });
    };

    // Submit feedback
    const handleFeedbackSubmit = async (comment) => {
        setFeedbackSubmitting(true);
        try {
            await submitFeedback({
                sessionId,
                rating: feedbackModal.rating,
                question: feedbackModal.question,
                response: feedbackModal.response,
                comment
            });

            // Mark this message as having feedback
            setFeedbackGiven(prev => ({
                ...prev,
                [feedbackModal.messageIndex]: feedbackModal.rating
            }));

            // Close modal
            setFeedbackModal({
                isOpen: false,
                rating: null,
                messageIndex: null,
                question: '',
                response: ''
            });
        } catch (error) {
            console.error('[CHAT] Feedback submission failed:', error);
            // Still close modal but don't mark as given
        } finally {
            setFeedbackSubmitting(false);
        }
    };

    // Close feedback modal
    const handleFeedbackClose = () => {
        setFeedbackModal({
            isOpen: false,
            rating: null,
            messageIndex: null,
            question: '',
            response: ''
        });
    };

    // Auto-resize textarea
    const handleTextareaChange = (e) => {
        setInputValue(e.target.value);
        e.target.style.height = 'auto';
        e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
    };

    // Send message
    const handleSendMessage = useCallback(async (messageText = null) => {
        const text = messageText || inputValue.trim();
        if (!text || isLoading) return;

        // Add user message
        const userMessage = { role: 'user', content: text };
        setMessages(prev => [...prev, userMessage]);
        setInputValue('');
        setIsLoading(true);

        // Reset textarea height
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }

        try {
            // Build message history for context
            const messageHistory = [...messages, userMessage].map(m => ({
                role: m.role,
                content: m.content
            }));

            const response = await sendChatMessage(messageHistory, sessionId, visibleTeamIds || [], language);

            // Add assistant response with entities and actions for interactive display
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: response.response,
                entities: response.entities || [],
                actions: response.actions || []
            }]);
        } catch (error) {
            console.error('[CHAT] Error:', error);
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: error.message || t('chat.error'),
                isError: true
            }]);
        } finally {
            setIsLoading(false);
        }
    }, [inputValue, isLoading, messages, sessionId, language, t, visibleTeamIds]);

    // Handle Enter key
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    // Handle suggested prompt click
    const handleSuggestedPrompt = (prompt) => {
        handleSendMessage(prompt);
    };

    // Render status indicator
    const renderStatusBar = () => {
        let statusText = t('chat.checkingService');
        let dotStyle = styles.statusDotLoading;

        if (serviceStatus) {
            if (serviceStatus.status === 'ready') {
                statusText = t('chat.connected');
                dotStyle = styles.statusDot;
            } else if (serviceStatus.status === 'not_configured') {
                statusText = t('chat.notConfigured');
                dotStyle = styles.statusDotError;
            } else if (serviceStatus.status === 'error' || serviceStatus.error) {
                statusText = t('chat.serviceUnavailable');
                dotStyle = styles.statusDotError;
            }
        }

        return (
            <div style={styles.statusBar}>
                <span style={{ ...styles.statusDot, ...dotStyle }}></span>
                <span>{statusText}</span>
            </div>
        );
    };

    // Render welcome message with suggested prompts
    const renderWelcome = () => {
        return (
            <div style={styles.welcomeMessage}>
                <div style={styles.welcomeTitle}>{t('chat.contextHelp.welcomeTitle')}</div>
                <div style={styles.welcomeText}>
                    {t('chat.contextHelp.welcomeText')}
                </div>
                <div style={styles.suggestedPrompts}>
                    {SUGGESTED_PROMPTS.map((prompt, index) => (
                        <button
                            key={index}
                            style={styles.suggestedPrompt}
                            onClick={() => handleSuggestedPrompt(prompt)}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = 'var(--border)';
                                e.target.style.borderColor = 'var(--muted)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'var(--border)';
                                e.target.style.borderColor = 'var(--border)';
                            }}
                        >
                            {prompt}
                        </button>
                    ))}
                </div>
            </div>
        );
    };

    // Render follow-up suggestions
    const renderFollowUps = () => {
        const context = detectContext(messages);
        const templateSuggestions = FOLLOW_UP_SUGGESTIONS[context] || FOLLOW_UP_SUGGESTIONS.default;

        // Get entities from the last assistant message to make suggestions contextual
        const lastAssistantMessage = [...messages].reverse().find(m => m.role === 'assistant');
        const entities = lastAssistantMessage?.entities || [];

        // Find first team and user entity names
        const firstTeam = entities.find(e => e.type === 'team');
        const firstUser = entities.find(e => e.type === 'user');
        const teamName = firstTeam?.name || 'this team';
        const userName = firstUser?.name || firstUser?.data?.user_email || 'this user';

        // Replace placeholders with actual names
        const suggestions = templateSuggestions.map(template => {
            let prompt = template;
            if (template.includes('{team}')) {
                // Only show team-specific prompts if we have a team entity
                if (!firstTeam) return null;
                prompt = prompt.replace(/{team}/g, teamName);
            }
            if (template.includes('{user}')) {
                // Only show user-specific prompts if we have a user entity
                if (!firstUser) return null;
                prompt = prompt.replace(/{user}/g, userName);
            }
            return prompt;
        }).filter(Boolean); // Remove null entries

        // If no suggestions remain after filtering, use defaults
        const finalSuggestions = suggestions.length > 0 ? suggestions : FOLLOW_UP_SUGGESTIONS.default;

        return (
            <div style={styles.followUpContainer}>
                <div style={styles.followUpLabel}>{t('chat.tryAsking')}</div>
                <div style={styles.suggestedPromptsInline}>
                    {finalSuggestions.map((prompt, index) => (
                        <button
                            key={index}
                            style={styles.suggestedPromptSmall}
                            onClick={() => handleSuggestedPrompt(prompt)}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = 'var(--border)';
                                e.target.style.borderColor = 'var(--border)';
                                e.target.style.color = 'var(--fg-primary)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'var(--bg)';
                                e.target.style.borderColor = 'var(--border)';
                                e.target.style.color = 'var(--fg-secondary)';
                            }}
                        >
                            {prompt}
                        </button>
                    ))}
                </div>
            </div>
        );
    };

    // Render message bubble
    const renderMessage = (message, index, isLast) => {
        const isUser = message.role === 'user';
        const messageStyle = {
            ...styles.message,
            ...(isUser ? styles.userMessage : styles.assistantMessage),
            ...(message.isError ? styles.errorMessage : {})
        };

        return (
            <React.Fragment key={index}>
                <div style={messageStyle}>
                    {/* Render markdown for assistant messages, plain text for user */}
                    {isUser ? (
                        message.content
                    ) : message.isError ? (
                        <div>
                            <div>{renderMarkdown(message.content)}</div>
                            {/* Show View User Manual link on error */}
                            {onOpenHelp && (
                                <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
                                    <button
                                        onClick={onOpenHelp}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: 'var(--signal-blue)',
                                            cursor: 'pointer',
                                            fontSize: '0.875rem',
                                            padding: 0,
                                            textDecoration: 'underline',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                        }}
                                    >
                                        📖 {t('chat.viewUserManual', 'View User Manual')}
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div>{renderMarkdown(message.content)}</div>
                    )}
                    {/* Render interactive action cards for assistant messages */}
                    {!isUser && message.actions && message.actions.length > 0 && (
                        <div style={{
                            marginTop: '12px',
                            paddingTop: '12px',
                            borderTop: '1px solid var(--border)'
                        }}>
                            <div style={{
                                fontSize: '11px',
                                color: 'var(--fg-secondary)',
                                marginBottom: '8px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em'
                            }}>
                                {t('chat.clickToOpen', 'Click to open:')}
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                {message.actions.map((action, idx) => (
                                    <ActionCard
                                        key={`action-${action.type}-${idx}`}
                                        action={action}
                                        onOpenSettings={onOpenSettings}
                                        onNavigate={onNavigate}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                    {/* Render interactive entity cards for assistant messages */}
                    {!isUser && message.entities && message.entities.length > 0 && (
                        <div style={{
                            marginTop: '12px',
                            paddingTop: '12px',
                            borderTop: '1px solid var(--border)'
                        }}>
                            <div style={{
                                fontSize: '11px',
                                color: 'var(--fg-secondary)',
                                marginBottom: '8px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em'
                            }}>
                                {t('chat.clickToView')}
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                {message.entities.map((entity, idx) => (
                                    <EntityCard
                                        key={`${entity.type}-${entity.id}-${idx}`}
                                        entity={entity}
                                        onTeamClick={(teamData) => {
                                            // Just open the modal - user can ask for help via "Ask AI" button in modal
                                            if (onOpenTeam) onOpenTeam(teamData);
                                        }}
                                        onUserClick={(userData) => {
                                            // Just open the modal - user can ask for help via "Ask AI" button in modal
                                            if (onOpenUser) onOpenUser(userData);
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                    {/* Feedback buttons for assistant messages */}
                    {!isUser && !message.isError && (
                        <div style={{
                            marginTop: '12px',
                            paddingTop: '8px',
                            borderTop: '1px solid var(--border)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <span style={{ fontSize: '11px', color: 'var(--fg-secondary)' }}>
                                {feedbackGiven[index]
                                    ? t('chat.feedback.thanks', 'Thanks for your feedback!')
                                    : t('chat.feedback.rate', 'Was this helpful?')
                                }
                            </span>
                            {!feedbackGiven[index] && (
                                <>
                                    <button
                                        onClick={() => handleFeedbackClick('positive', index)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: '16px',
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            transition: 'background-color 0.2s',
                                            opacity: 0.7
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.backgroundColor = 'rgba(34, 197, 94, 0.2)';
                                            e.target.style.opacity = '1';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.backgroundColor = 'transparent';
                                            e.target.style.opacity = '0.7';
                                        }}
                                        title={t('chat.feedback.helpful', 'This was helpful')}
                                    >
                                        👍
                                    </button>
                                    <button
                                        onClick={() => handleFeedbackClick('negative', index)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: '16px',
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            transition: 'background-color 0.2s',
                                            opacity: 0.7
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.backgroundColor = 'rgba(239, 68, 68, 0.2)';
                                            e.target.style.opacity = '1';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.backgroundColor = 'transparent';
                                            e.target.style.opacity = '0.7';
                                        }}
                                        title={t('chat.feedback.notHelpful', 'This was not helpful')}
                                    >
                                        👎
                                    </button>
                                </>
                            )}
                            {feedbackGiven[index] && (
                                <span style={{ fontSize: '14px' }}>
                                    {feedbackGiven[index] === 'positive' ? '👍' : '👎'}
                                </span>
                            )}
                        </div>
                    )}
                </div>
                {/* Show follow-up suggestions after the last assistant message */}
                {!isUser && isLast && !isLoading && renderFollowUps()}
            </React.Fragment>
        );
    };

    // Render typing indicator
    const renderTypingIndicator = () => (
        <div style={styles.typingIndicator}>
            <span style={{ ...styles.typingDot, animationDelay: '0s' }}></span>
            <span style={{ ...styles.typingDot, animationDelay: '0.2s' }}></span>
            <span style={{ ...styles.typingDot, animationDelay: '0.4s' }}></span>
        </div>
    );

    const isServiceReady = serviceStatus?.status === 'ready';
    const canSend = inputValue.trim() && !isLoading && isServiceReady;
    const hasMessages = messages.length > 0;

    return (
        <>
            {/* Overlay */}
            <div
                style={{
                    ...styles.overlay,
                    ...(isOpen ? styles.overlayOpen : {})
                }}
                onClick={onClose}
            />

            {/* Sidebar */}
            <div
                style={{
                    ...styles.sidebar,
                    ...(isOpen ? styles.sidebarOpen : {})
                }}
            >
                {/* Header */}
                <div style={styles.header}>
                    <div style={styles.headerLeft}>
                        <h3 style={styles.headerTitle}>
                            {t('chat.title')}
                        </h3>
                        {hasMessages && (
                            <button
                                style={styles.newChatButton}
                                onClick={handleNewChat}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#2563eb';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'var(--border)';
                                }}
                                title={t('chat.newChatTooltip')}
                            >
                                + {t('chat.new')}
                            </button>
                        )}
                    </div>
                    <button
                        style={styles.closeButton}
                        onClick={onClose}
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = 'var(--border)';
                            e.target.style.color = 'var(--fg-primary)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.color = 'var(--fg-secondary)';
                        }}
                        aria-label="Close chat"
                    >
                        x
                    </button>
                </div>

                {/* Status bar */}
                {renderStatusBar()}

                {/* Messages */}
                <div style={styles.messagesContainer}>
                    {messages.length === 0 ? (
                        renderWelcome()
                    ) : (
                        <>
                            {messages.map((message, index) =>
                                renderMessage(message, index, index === messages.length - 1)
                            )}
                            {isLoading && renderTypingIndicator()}
                        </>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div style={styles.inputContainer}>
                    <div style={styles.inputWrapper}>
                        <textarea
                            ref={textareaRef}
                            style={styles.textarea}
                            value={inputValue}
                            onChange={handleTextareaChange}
                            onKeyDown={handleKeyDown}
                            placeholder={isServiceReady ? t('chat.placeholder') : t('chat.serviceUnavailable')}
                            disabled={!isServiceReady}
                            rows={1}
                        />
                        <button
                            style={{
                                ...styles.sendButton,
                                ...(canSend ? {} : styles.sendButtonDisabled)
                            }}
                            onClick={() => handleSendMessage()}
                            disabled={!canSend}
                            onMouseEnter={(e) => {
                                if (canSend) e.target.style.backgroundColor = '#2563eb';
                            }}
                            onMouseLeave={(e) => {
                                if (canSend) e.target.style.backgroundColor = 'var(--border)';
                            }}
                            aria-label={t('chat.sendMessage')}
                        >
                            {t('chat.send')}
                        </button>
                    </div>
                </div>

                {/* Disclaimer */}
                <div style={styles.disclaimer}>
                    {t('chat.disclaimer')}
                </div>
            </div>

            {/* Animations */}
            <style>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }
                @keyframes typing {
                    0%, 60%, 100% { transform: translateY(0); }
                    30% { transform: translateY(-4px); }
                }
            `}</style>

            {/* Feedback Modal */}
            <FeedbackModal
                isOpen={feedbackModal.isOpen}
                onClose={handleFeedbackClose}
                onSubmit={handleFeedbackSubmit}
                rating={feedbackModal.rating}
                question={feedbackModal.question}
                response={feedbackModal.response}
                isSubmitting={feedbackSubmitting}
            />
        </>
    );
};

ChatSidebar.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onOpenTeam: PropTypes.func,
    onOpenUser: PropTypes.func,
    onOpenSettings: PropTypes.func,
    onNavigate: PropTypes.func,
    onOpenHelp: PropTypes.func,
    visibleTeamIds: PropTypes.arrayOf(PropTypes.string)
};

ChatSidebar.defaultProps = {
    onOpenTeam: null,
    onOpenUser: null,
    onOpenSettings: null,
    onNavigate: null,
    onOpenHelp: null
};

export default ChatSidebar;

